import { useState } from 'react';
import { useReportBuilderStore } from '../../stores/reportBuilderStore';
import { Play, ArrowCounterClockwise, CircleNotch, FloppyDisk, Table, ChartBar, PaintBrush, CalendarBlank } from '@phosphor-icons/react';
import ConditionalFormatPanel from './ConditionalFormatPanel';
import PeriodComparisonPanel from './PeriodComparisonPanel';
import { FEATURE_FLAGS } from '../../constants/featureFlags';

export default function QueryToolbar() {
  const {
    buildQuerySpec, executeQuery, resetQuery, setSaveDialogOpen,
    queryLoading, queryError, results, conditionalFormats, periodComparison,
    viewMode, setViewMode, autoDetectFieldMapping, autoDetectPivotConfig,
  } = useReportBuilderStore();

  const [showFormatPanel, setShowFormatPanel] = useState(false);
  const [showPeriodPanel, setShowPeriodPanel] = useState(false);

  const canRun = buildQuerySpec() !== null && !queryLoading;
  const canSave = results !== null && !queryLoading;

  const handleChartMode = () => {
    if (viewMode !== 'chart') {
      autoDetectFieldMapping();
      setViewMode('chart');
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Primary actions */}
      <button onClick={executeQuery} disabled={!canRun}
        className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed">
        {queryLoading ? <CircleNotch className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" weight="fill" />}
        Run
      </button>

      <button onClick={() => setSaveDialogOpen(true)} disabled={!canSave}
        className="flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
        <FloppyDisk className="h-4 w-4" /> Save
      </button>

      <button onClick={resetQuery}
        className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100">
        <ArrowCounterClockwise className="h-4 w-4" /> Reset
      </button>

      <div className="h-5 w-px bg-gray-200" />

      {/* View mode toggle */}
      <div className="inline-flex rounded-md border border-gray-300 bg-white">
        <button onClick={() => setViewMode('table')}
          className={`flex items-center gap-1 rounded-l-md px-2.5 py-1.5 text-sm font-medium transition-colors ${
            viewMode === 'table' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'
          }`}>
          <Table className="h-3.5 w-3.5" /> Table
        </button>
        {FEATURE_FLAGS.PIVOT_TABLE && (
          <button onClick={() => { autoDetectPivotConfig(); setViewMode('pivot'); }}
            className={`flex items-center gap-1 border-x border-gray-300 px-2.5 py-1.5 text-sm font-medium transition-colors ${
              viewMode === 'pivot' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}>
            <Table className="h-3.5 w-3.5" /> Pivot
          </button>
        )}
        <button onClick={handleChartMode}
          className={`flex items-center gap-1 rounded-r-md px-2.5 py-1.5 text-sm font-medium transition-colors ${
            viewMode === 'chart' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'
          }`}>
          <ChartBar className="h-3.5 w-3.5" /> Chart
        </button>
      </div>

      {/* Conditional formatting */}
      {FEATURE_FLAGS.CONDITIONAL_FORMATTING && results && viewMode === 'table' && (
        <button onClick={() => setShowFormatPanel(true)}
          className={`flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-sm font-medium transition-colors ${
            conditionalFormats.length > 0 ? 'border-purple-300 bg-purple-50 text-purple-700' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
          }`}>
          <PaintBrush className="h-3.5 w-3.5" />
          Format{conditionalFormats.length > 0 && ` (${conditionalFormats.length})`}
        </button>
      )}

      {FEATURE_FLAGS.CONDITIONAL_FORMATTING && showFormatPanel && <ConditionalFormatPanel onClose={() => setShowFormatPanel(false)} />}

      {/* Period comparison */}
      {FEATURE_FLAGS.PERIOD_COMPARISON && canRun && (
        <button onClick={() => setShowPeriodPanel(true)}
          className={`flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-sm font-medium transition-colors ${
            periodComparison ? 'border-teal-300 bg-teal-50 text-teal-700' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
          }`}>
          <CalendarBlank className="h-3.5 w-3.5" />
          Compare{periodComparison ? ' (on)' : ''}
        </button>
      )}

      {FEATURE_FLAGS.PERIOD_COMPARISON && showPeriodPanel && <PeriodComparisonPanel onClose={() => setShowPeriodPanel(false)} />}

      {/* Push right */}
      <div className="flex-1" />

      {/* Results stats */}
      {results && !queryLoading && (
        <span className="text-xs text-gray-500">
          {results.query_stats.row_count} rows | {results.query_stats.query_time_ms}ms
          {results.pagination && ` (${results.pagination.total} total)`}
        </span>
      )}

      {/* Error inline */}
      {queryError && (
        <div className="max-w-sm truncate rounded bg-red-50 px-2.5 py-1 text-xs text-red-700" title={queryError}>
          {queryError}
        </div>
      )}
    </div>
  );
}
